import { useAppStore } from '../store/appStore';
import { useMemo } from 'react';
import type { WatchHistoryEntry } from '@kidstube/shared';

interface DayData {
    date: string;
    label: string;
    seconds: number;
}

interface DailyWatchChartProps {
    history?: WatchHistoryEntry[];
    dailyTotals?: Array<{ date: string; seconds: number }>;
}

function formatWatchDuration(seconds: number): string {
    const wholeSeconds = Math.max(0, Math.floor(seconds));
    if (wholeSeconds < 60) return `${wholeSeconds}s`;

    const minutes = Math.floor(wholeSeconds / 60);
    const remainingSeconds = wholeSeconds % 60;
    if (minutes < 60) {
        return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function localDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function DailyWatchChart({ history, dailyTotals }: DailyWatchChartProps) {
    const storeWatchHistory = useAppStore(state => state.watchHistory);
    const watchHistory = history || storeWatchHistory;

    const dailyData = useMemo<DayData[]>(() => {
        const now = new Date();
        const days: DayData[] = [];

        // Generate last 7 days
        for (let i = 6; i >= 0; i--) {
            const date = new Date(now);
            date.setDate(date.getDate() - i);
            date.setHours(0, 0, 0, 0);

            const dateStr = localDateKey(date);
            const label = i === 0 ? 'Today' :
                i === 1 ? 'Yesterday' :
                    date.toLocaleDateString('en-US', { weekday: 'short' });

            days.push({ date: dateStr, label, seconds: 0 });
        }

        if (dailyTotals) {
            for (const total of dailyTotals) {
                const day = days.find(candidate => candidate.date === total.date);
                if (day) day.seconds = Math.max(0, Math.floor(total.seconds || 0));
            }
        } else {
            // Retained for isolated component use. The Parent Dashboard always
            // supplies server-aggregated playback events, never progress rows.
            watchHistory.forEach(entry => {
                const entryDate = new Date(entry.watchedAt);
                entryDate.setHours(0, 0, 0, 0);
                const entryDateStr = localDateKey(entryDate);

                const day = days.find(d => d.date === entryDateStr);
                if (day) {
                    day.seconds += Math.max(0, Math.floor(entry.watchedDurationSeconds || 0));
                }
            });
        }

        return days;
    }, [dailyTotals, watchHistory]);

    const maxSeconds = Math.max(...dailyData.map(d => d.seconds), 1);
    const todaySeconds = dailyData[6]?.seconds || 0;
    const weekSeconds = dailyData.reduce((sum, d) => sum + d.seconds, 0);

    return (
        <div style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.08)', borderRadius: '16px' }}>
            <h2 style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '1rem' }}>
                Daily Watch Time (Last 7 Days)
            </h2>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: '150px' }}>
                {dailyData.map((day) => {
                    const heightPercent = maxSeconds > 0 ? (day.seconds / maxSeconds) * 100 : 0;

                    return (
                        <div
                            key={day.date}
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}
                        >
                            {/* Bar */}
                            <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end'
                            }}>
                                <div
                                    style={{
                                        width: '100%',
                                        height: `${heightPercent}%`,
                                        background: day.label === 'Today'
                                            ? 'linear-gradient(135deg, #6c5ce7, #fd79a8)'
                                            : 'linear-gradient(135deg, rgba(108,92,231,0.6), rgba(253,121,168,0.6))',
                                        borderRadius: '8px 8px 0 0',
                                        minHeight: day.seconds > 0 ? '8px' : '0',
                                        transition: 'height 0.3s ease',
                                        position: 'relative'
                                    }}
                                    title={formatWatchDuration(day.seconds)}
                                >
                                    {/* Duration label on top of bar */}
                                    {day.seconds > 0 && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '-1.5rem',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            color: 'rgba(255,255,255,0.9)',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {formatWatchDuration(day.seconds)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Day label */}
                            <div style={{
                                fontSize: '0.75rem',
                                color: day.label === 'Today'
                                    ? 'rgba(255,255,255,0.9)'
                                    : 'rgba(255,255,255,0.6)',
                                fontWeight: day.label === 'Today' ? 600 : 400,
                                textAlign: 'center'
                            }}>
                                {day.label}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Summary */}
            <div style={{
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                gap: '1.5rem',
                fontSize: '0.85rem',
                color: 'rgba(255,255,255,0.7)'
            }}>
                <div>
                    <strong style={{ color: 'rgba(255,255,255,0.9)' }}>
                        {formatWatchDuration(todaySeconds)}
                    </strong> today
                </div>
                <div>
                    <strong style={{ color: 'rgba(255,255,255,0.9)' }}>
                        {formatWatchDuration(weekSeconds)}
                    </strong> this week
                </div>
            </div>
        </div>
    );
}
